<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { useAlert } from 'dashboard/composables';
import CommercialIntegrationsAPI from 'dashboard/api/commercialIntegrations';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';

const { t } = useI18n();
const isLoading = ref(false);
const isSaving = ref(false);
const isTesting = ref(false);
const events = ref([]);
const form = reactive({
  enabled: false,
  n8nWebhookUrl: '',
  n8nSecret: '',
  secretConfigured: false,
  botzAiEnabled: false,
  whatsappActionMode: 'disabled',
  eventsEnabled: {},
  testMode: true,
  lastTestedAt: null,
  lastTestStatus: null,
  lastTestError: null,
});

const eventOptions = [
  'action_created',
  'follow_up_created',
  'action_completed',
  'deal_stage_changed',
  'deal_at_risk',
  'hot_opportunity',
];
const eventLabel = event =>
  ({
    action_created: t('COMMERCIAL.INTEGRATIONS.EVENTS.ACTION_CREATED'),
    follow_up_created: t('COMMERCIAL.INTEGRATIONS.EVENTS.FOLLOW_UP_CREATED'),
    action_completed: t('COMMERCIAL.INTEGRATIONS.EVENTS.ACTION_COMPLETED'),
    deal_stage_changed: t('COMMERCIAL.INTEGRATIONS.EVENTS.DEAL_STAGE_CHANGED'),
    deal_at_risk: t('COMMERCIAL.INTEGRATIONS.EVENTS.DEAL_AT_RISK'),
    hot_opportunity: t('COMMERCIAL.INTEGRATIONS.EVENTS.HOT_OPPORTUNITY'),
    connection_test: t('COMMERCIAL.INTEGRATIONS.EVENTS.CONNECTION_TEST'),
  })[event] || event;
const statusLabel = status =>
  ({
    pending: t('COMMERCIAL.INTEGRATIONS.STATUS.PENDING'),
    success: t('COMMERCIAL.INTEGRATIONS.STATUS.SUCCESS'),
    failed: t('COMMERCIAL.INTEGRATIONS.STATUS.FAILED'),
    skipped: t('COMMERCIAL.INTEGRATIONS.STATUS.SKIPPED'),
  })[status] || status;
const statusClass = status =>
  ({
    success: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    skipped: 'bg-n-amber-3 text-n-amber-11',
    pending: 'bg-n-blue-3 text-n-blue-11',
  })[status];
const lastTestDescription = computed(() => {
  if (!form.lastTestedAt) return t('COMMERCIAL.INTEGRATIONS.NEVER_TESTED');
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(form.lastTestedAt));
});
const dateTime = value =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
const prepareSecretEdit = () => {
  if (form.n8nSecret.includes('•')) form.n8nSecret = '';
};

const applyPayload = payload => {
  const normalized = camelcaseKeys(payload, { deep: true });
  Object.assign(form, normalized);
  if (normalized.events) events.value = normalized.events;
};
const loadConfiguration = async () => {
  isLoading.value = true;
  try {
    const { data } = await CommercialIntegrationsAPI.get();
    applyPayload(data.payload);
  } catch {
    useAlert(t('COMMERCIAL.INTEGRATIONS.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};
const saveConfiguration = async () => {
  isSaving.value = true;
  try {
    const attributes = {
      enabled: form.enabled,
      n8nWebhookUrl: form.n8nWebhookUrl,
      botzAiEnabled: form.botzAiEnabled,
      whatsappActionMode: form.whatsappActionMode,
      eventsEnabled: form.eventsEnabled,
      testMode: form.testMode,
    };
    if (form.n8nSecret && !form.n8nSecret.includes('•')) {
      attributes.n8nSecret = form.n8nSecret;
    }
    const { data } = await CommercialIntegrationsAPI.updateConfiguration(
      snakecaseKeys(attributes, { deep: true })
    );
    applyPayload(data.payload);
    useAlert(t('COMMERCIAL.INTEGRATIONS.MESSAGES.SAVED'));
  } catch {
    useAlert(t('COMMERCIAL.INTEGRATIONS.MESSAGES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
const loadEvents = async () => {
  const { data } = await CommercialIntegrationsAPI.events();
  events.value = camelcaseKeys(data.payload, { deep: true });
};
const testConnection = async () => {
  isTesting.value = true;
  try {
    const { data } = await CommercialIntegrationsAPI.testConnection();
    applyPayload(data.payload.setting);
    await loadEvents();
    useAlert(
      data.payload.status === 'success'
        ? t('COMMERCIAL.INTEGRATIONS.MESSAGES.TEST_SUCCESS')
        : t('COMMERCIAL.INTEGRATIONS.MESSAGES.TEST_NOT_SENT')
    );
  } catch {
    useAlert(t('COMMERCIAL.INTEGRATIONS.MESSAGES.TEST_ERROR'));
  } finally {
    isTesting.value = false;
  }
};
onMounted(loadConfiguration);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="t('COMMERCIAL.INTEGRATIONS.TITLE')"
        :description="t('COMMERCIAL.INTEGRATIONS.DESCRIPTION')"
      >
        <template #actions>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              :disabled="isTesting || isSaving"
              class="inline-flex items-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
              @click="testConnection"
            >
              <span class="i-lucide-plug-zap size-4" />
              {{ t('COMMERCIAL.INTEGRATIONS.TEST') }}
            </button>
            <button
              type="button"
              :disabled="isSaving || isTesting"
              class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:bg-n-brand-hover disabled:opacity-50"
              @click="saveConfiguration"
            >
              <span class="i-lucide-save size-4" />
              {{ t('COMMERCIAL.INTEGRATIONS.SAVE') }}
            </button>
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="space-y-6">
        <section
          class="rounded-2xl border border-n-weak bg-n-background p-5 shadow-sm"
        >
          <div class="grid gap-5 lg:grid-cols-2">
            <label
              class="flex items-center justify-between gap-4 rounded-xl border border-n-weak p-4"
            >
              <span>
                <span class="block font-medium text-n-slate-12">{{
                  t('COMMERCIAL.INTEGRATIONS.FIELDS.ENABLED')
                }}</span>
                <span class="mt-1 block text-xs text-n-slate-9">{{
                  t('COMMERCIAL.INTEGRATIONS.FIELDS.ENABLED_HINT')
                }}</span>
              </span>
              <input
                v-model="form.enabled"
                type="checkbox"
                class="size-5 rounded border-n-weak text-n-brand"
              />
            </label>
            <label
              class="flex items-center justify-between gap-4 rounded-xl border border-n-weak p-4"
            >
              <span>
                <span class="block font-medium text-n-slate-12">{{
                  t('COMMERCIAL.INTEGRATIONS.FIELDS.TEST_MODE')
                }}</span>
                <span class="mt-1 block text-xs text-n-slate-9">{{
                  t('COMMERCIAL.INTEGRATIONS.FIELDS.TEST_MODE_HINT')
                }}</span>
              </span>
              <input
                v-model="form.testMode"
                type="checkbox"
                class="size-5 rounded border-n-weak text-n-brand"
              />
            </label>
            <label class="flex flex-col gap-1.5 text-sm text-n-slate-11">
              {{ t('COMMERCIAL.INTEGRATIONS.FIELDS.WEBHOOK_URL') }}
              <input
                v-model.trim="form.n8nWebhookUrl"
                type="url"
                :placeholder="
                  t('COMMERCIAL.INTEGRATIONS.FIELDS.WEBHOOK_PLACEHOLDER')
                "
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1.5 text-sm text-n-slate-11">
              {{ t('COMMERCIAL.INTEGRATIONS.FIELDS.SECRET') }}
              <input
                v-model="form.n8nSecret"
                type="password"
                autocomplete="new-password"
                :placeholder="t('COMMERCIAL.INTEGRATIONS.FIELDS.SECRET_HINT')"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                @focus="prepareSecretEdit"
              />
            </label>
            <label
              class="flex items-center justify-between gap-4 rounded-xl border border-n-weak p-4"
            >
              <span>
                <span class="block font-medium text-n-slate-12">{{
                  t('COMMERCIAL.INTEGRATIONS.FIELDS.BOTZ_AI')
                }}</span>
                <span class="mt-1 block text-xs text-n-slate-9">{{
                  t('COMMERCIAL.INTEGRATIONS.FIELDS.BOTZ_AI_HINT')
                }}</span>
              </span>
              <input
                v-model="form.botzAiEnabled"
                type="checkbox"
                class="size-5 rounded border-n-weak text-n-brand"
              />
            </label>
            <label class="flex flex-col gap-1.5 text-sm text-n-slate-11">
              {{ t('COMMERCIAL.INTEGRATIONS.FIELDS.WHATSAPP_MODE') }}
              <select
                v-model="form.whatsappActionMode"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
              >
                <option value="disabled">
                  {{ t('COMMERCIAL.INTEGRATIONS.WHATSAPP.DISABLED') }}
                </option>
                <option value="draft_only">
                  {{ t('COMMERCIAL.INTEGRATIONS.WHATSAPP.DRAFT_ONLY') }}
                </option>
                <option value="manual_confirm">
                  {{ t('COMMERCIAL.INTEGRATIONS.WHATSAPP.MANUAL_CONFIRM') }}
                </option>
              </select>
            </label>
          </div>
        </section>

        <section
          class="rounded-2xl border border-n-weak bg-n-background p-5 shadow-sm"
        >
          <h2 class="font-semibold text-n-slate-12">
            {{ t('COMMERCIAL.INTEGRATIONS.EVENTS_TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-9">
            {{ t('COMMERCIAL.INTEGRATIONS.EVENTS_HINT') }}
          </p>
          <div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label
              v-for="event in eventOptions"
              :key="event"
              class="flex items-center gap-2 rounded-lg border border-n-weak p-3 text-sm text-n-slate-11"
            >
              <input
                v-model="form.eventsEnabled[event]"
                type="checkbox"
                class="size-4 rounded border-n-weak text-n-brand"
              />
              {{ eventLabel(event) }}
            </label>
          </div>
        </section>

        <section
          class="rounded-2xl border border-n-weak bg-n-background p-5 shadow-sm"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 class="font-semibold text-n-slate-12">
                {{ t('COMMERCIAL.INTEGRATIONS.LAST_TEST') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-9">
                {{ lastTestDescription }}
              </p>
            </div>
            <span
              v-if="form.lastTestStatus"
              class="rounded-full px-2.5 py-1 text-xs font-medium"
              :class="statusClass(form.lastTestStatus)"
            >
              {{ statusLabel(form.lastTestStatus) }}
            </span>
          </div>
          <p v-if="form.lastTestError" class="mt-3 text-sm text-n-ruby-11">
            {{ form.lastTestError }}
          </p>
        </section>

        <section
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-background shadow-sm"
        >
          <div class="border-b border-n-weak p-5">
            <h2 class="font-semibold text-n-slate-12">
              {{ t('COMMERCIAL.INTEGRATIONS.LOGS_TITLE') }}
            </h2>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[760px] text-left text-sm">
              <thead class="bg-n-alpha-2 text-xs text-n-slate-9">
                <tr>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.INTEGRATIONS.LOGS.DATE') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.INTEGRATIONS.LOGS.EVENT') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.INTEGRATIONS.LOGS.DEAL') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.INTEGRATIONS.LOGS.STATUS') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ t('COMMERCIAL.INTEGRATIONS.LOGS.RESPONSE') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="event in events"
                  :key="event.id"
                  class="border-t border-n-weak"
                >
                  <td class="whitespace-nowrap px-4 py-3 text-n-slate-9">
                    {{ dateTime(event.createdAt) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ eventLabel(event.eventType) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-10">
                    {{
                      event.dealName ||
                      t('COMMERCIAL.INTEGRATIONS.LOGS.EMPTY_VALUE')
                    }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="rounded-full px-2 py-1 text-xs font-medium"
                      :class="statusClass(event.status)"
                    >
                      {{ statusLabel(event.status) }}
                    </span>
                  </td>
                  <td class="max-w-xs px-4 py-3 text-xs text-n-slate-9">
                    {{
                      event.errorMessage ||
                      event.responseStatus ||
                      t('COMMERCIAL.INTEGRATIONS.LOGS.NO_RESPONSE')
                    }}
                  </td>
                </tr>
                <tr v-if="!events.length">
                  <td colspan="5" class="px-4 py-10 text-center text-n-slate-9">
                    {{ t('COMMERCIAL.INTEGRATIONS.LOGS.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
